// src/stores/expenseStore.js
import { create } from 'zustand';
import * as expenseService from '../services/expenseService';
import useNotificationStore from './notificationStore';

const useExpenseStore = create((set, get) => ({
  // State
  expenses: [],
  currentExpense: null,
  isLoading: false,
  criticalError: null, // Only for expense loading failures
  operationError: null, // For non-critical operations
  statistics: null,
  meta: {},  // Add meta object for EntityListContainer
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  },
  filters: {
    status: 'all',
    category: 'all',
    clientId: 'all',
    projectId: 'all',
    billable: 'all',
    dateRange: {
      startDate: null,
      endDate: null
    },
    search: ''
  },
  sort: {
    field: 'date',
    order: 'desc'
  },

  // Actions
  setFilters: (filters) => set({ 
    filters: { ...get().filters, ...filters },
    pagination: { ...get().pagination, page: 1 }
  }),

  resetFilters: () => set({ 
    filters: {
      status: 'all',
      category: 'all',
      clientId: 'all',
      projectId: 'all',
      billable: 'all',
      dateRange: {
        startDate: null,
        endDate: null
      },
      search: ''
    },
    pagination: { ...get().pagination, page: 1 }
  }),

  setSort: (sort) => set({ 
    sort,
    pagination: { ...get().pagination, page: 1 }
  }),

  setPage: (page) => set({ 
    pagination: { ...get().pagination, page } 
  }),

  // Fetch expenses with pagination and filters
  fetchExpenses: async (externalParams = {}) => {
    set({ isLoading: true, criticalError: null });
    
    try {
      const { filters, pagination, sort } = get();
      
      // Build query parameters - use external params if provided, otherwise use store state
      const params = {
        page: externalParams.page || pagination.page,
        limit: externalParams.limit || pagination.limit,
        sort: externalParams.sort || `${sort.order === 'desc' ? '-' : ''}${sort.field}`,
        ...externalParams
      };
      
      // If no external params, add filters from store
      if (!Object.keys(externalParams).length) {
        // Add filters to params
        if (filters.status && filters.status !== 'all') {
          params.status = filters.status;
        }
        
        if (filters.category && filters.category !== 'all') {
          params.category = filters.category;
        }
        
        if (filters.clientId && filters.clientId !== 'all') {
          params.clientId = filters.clientId;
        }
        
        if (filters.projectId && filters.projectId !== 'all') {
          params.projectId = filters.projectId;
        }
        
        if (filters.billable !== 'all') {
          params.billable = filters.billable;
        }
        
        if (filters.dateRange.startDate) {
          params.startDate = filters.dateRange.startDate;
        }
        
        if (filters.dateRange.endDate) {
          params.endDate = filters.dateRange.endDate;
        }
        
        if (filters.search) {
          params.search = filters.search;
        }
      }
      
      const response = await expenseService.getExpenses(params);
      
      // The response should now have the full structure: {status, meta, data}
      if (!response || !response.data) {
        console.error('Invalid response structure');
        set({
          expenses: [],
          meta: {},
          pagination: {
            ...get().pagination,
            total: 0,
            totalPages: 0
          },
          isLoading: false
        });
        return {
          data: { expenses: [] },
          meta: { total: 0, totalPages: 0 }
        };
      }
      
      // Extract expenses from the correct path
      const expenses = response.data?.expenses || [];
      const meta = response.meta || {};
      
      set({
        expenses,
        meta,
        pagination: {
          ...get().pagination,
          total: meta.total || 0,
          totalPages: meta.totalPages || 1,
          page: meta.page || 1
        },
        isLoading: false
      });
      
      // Return data in the format EntityListContainer expects (like clientStore)
      return response.data;
    } catch (error) {
      set({ 
        criticalError: error.message, 
        isLoading: false,
        expenses: [],
        pagination: {
          ...get().pagination,
          total: 0,
          totalPages: 0
        }
      });
      console.error('Error fetching expenses:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to fetch expenses');
      
      // Return consistent response structure even on error
      return {
        data: { expenses: [] },
        meta: { total: 0, totalPages: 0 }
      };
    }
  },

  // Fetch a single expense by ID
  fetchExpenseById: async (id) => {
    set({ isLoading: true, criticalError: null });
    
    try {
      const expense = await expenseService.getExpenseById(id);
      console.log("🚀 ~ fetchExpenseById: ~ expense:", expense)
      set({ currentExpense: expense.data.expense, isLoading: false });
      return expense;
    } catch (error) {
      set({ criticalError: error.message, isLoading: false });
      console.error('Error fetching expense:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to fetch expense details');
      
      // Don't throw error - return null
      return null;
    }
  },

  // Create a new expense
  createExpense: async (expenseData) => {
    set({ isLoading: true, operationError: null });
    
    try {
      const newExpense = await expenseService.createExpense(expenseData);
      
      // Add new expense to the beginning of the list
      const expenses = get().expenses;
      set({ 
        expenses: [newExpense, ...expenses],
        isLoading: false 
      });
      
      return newExpense;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.error('Error creating expense:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to create expense');
      
      throw error; // Keep throwing for create operations
    }
  },

  // Update an expense
  updateExpense: async (id, updates) => {
    set({ isLoading: true, operationError: null });
    
    try {
      const updatedExpense = await expenseService.updateExpense(id, updates);
      
      // Update expense in the list
      const expenses = get().expenses.map(exp => 
        exp._id === id ? updatedExpense : exp
      );
      
      // Update current expense if it's the one being updated
      const currentExpense = get().currentExpense;
      if (currentExpense && currentExpense._id === id) {
        set({ currentExpense: updatedExpense });
      }
      
      set({ expenses, isLoading: false });
      return updatedExpense;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.error('Error updating expense:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to update expense');
      
      throw error; // Keep throwing for update operations
    }
  },

  // Delete an expense
  deleteExpense: async (id) => {
    set({ isLoading: true, operationError: null });
    
    try {
      await expenseService.deleteExpense(id);
      
      // Remove expense from the list
      const expenses = get().expenses.filter(exp => exp._id !== id);
      
      // Clear current expense if it's the one being deleted
      const currentExpense = get().currentExpense;
      if (currentExpense && currentExpense._id === id) {
        set({ currentExpense: null });
      }
      
      set({ expenses, isLoading: false });
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.error('Error deleting expense:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to delete expense');
      
      throw error; // Keep throwing for delete operations
    }
  },

  // Submit expense for approval
  submitForApproval: async (id, approverId) => {
    set({ isLoading: true, operationError: null });
    
    try {
      const updatedExpense = await expenseService.submitExpense(id, approverId);
      
      // Update expense in the list
      const expenses = get().expenses.map(exp => 
        exp._id === id ? updatedExpense : exp
      );
      
      // Update current expense if it's the one being submitted
      const currentExpense = get().currentExpense;
      if (currentExpense && currentExpense._id === id) {
        set({ currentExpense: updatedExpense });
      }
      
      set({ expenses, isLoading: false });
      return updatedExpense;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.error('Error submitting expense:', error);
      throw error;
    }
  },

  // Approve expense
  approveExpense: async (id, comment) => {
    set({ isLoading: true, operationError: null });
    
    try {
      const updatedExpense = await expenseService.approveExpense(id, comment);
      
      // Update expense in the list
      const expenses = get().expenses.map(exp => 
        exp._id === id ? updatedExpense : exp
      );
      
      // Update current expense if it's the one being approved
      const currentExpense = get().currentExpense;
      if (currentExpense && currentExpense._id === id) {
        set({ currentExpense: updatedExpense });
      }
      
      set({ expenses, isLoading: false });
      return updatedExpense;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.error('Error approving expense:', error);
      throw error;
    }
  },

  // Reject expense
  rejectExpense: async (id, comment) => {
    set({ isLoading: true, operationError: null });
    
    try {
      const updatedExpense = await expenseService.rejectExpense(id, comment);
      
      // Update expense in the list
      const expenses = get().expenses.map(exp => 
        exp._id === id ? updatedExpense : exp
      );
      
      // Update current expense if it's the one being rejected
      const currentExpense = get().currentExpense;
      if (currentExpense && currentExpense._id === id) {
        set({ currentExpense: updatedExpense });
      }
      
      set({ expenses, isLoading: false });
      return updatedExpense;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.error('Error rejecting expense:', error);
      throw error;
    }
  },

  // Mark expense as reimbursed
  markAsReimbursed: async (id, reimbursementData) => {
    set({ isLoading: true, operationError: null });
    
    try {
      const updatedExpense = await expenseService.reimburseExpense(id, reimbursementData);
      
      // Update expense in the list
      const expenses = get().expenses.map(exp => 
        exp._id === id ? updatedExpense : exp
      );
      
      // Update current expense if it's the one being reimbursed
      const currentExpense = get().currentExpense;
      if (currentExpense && currentExpense._id === id) {
        set({ currentExpense: updatedExpense });
      }
      
      set({ expenses, isLoading: false });
      return updatedExpense;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.error('Error reimbursing expense:', error);
      throw error;
    }
  },

  // Upload receipt
  uploadReceipt: async (expenseId, file) => {
    set({ isLoading: true, operationError: null });
    
    try {
      const receipt = await expenseService.uploadReceipt(expenseId, file);
      
      // Update expense with new receipt
      const expenses = get().expenses.map(exp => {
        if (exp._id === expenseId) {
          return {
            ...exp,
            receipts: [...exp.receipts, receipt]
          };
        }
        return exp;
      });
      
      // Update current expense if it's the one getting a new receipt
      const currentExpense = get().currentExpense;
      if (currentExpense && currentExpense._id === expenseId) {
        set({ 
          currentExpense: {
            ...currentExpense,
            receipts: [...currentExpense.receipts, receipt]
          }
        });
      }
      
      set({ expenses, isLoading: false });
      return receipt;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.error('Error uploading receipt:', error);
      throw error;
    }
  },

  // Delete receipt
  deleteReceipt: async (expenseId, receiptId) => {
    set({ isLoading: true, operationError: null });
    
    try {
      await expenseService.deleteReceipt(expenseId, receiptId);
      
      // Update expense without the deleted receipt
      const expenses = get().expenses.map(exp => {
        if (exp._id === expenseId) {
          return {
            ...exp,
            receipts: exp.receipts.filter(r => r._id !== receiptId)
          };
        }
        return exp;
      });
      
      // Update current expense if it's the one losing a receipt
      const currentExpense = get().currentExpense;
      if (currentExpense && currentExpense._id === expenseId) {
        set({ 
          currentExpense: {
            ...currentExpense,
            receipts: currentExpense.receipts.filter(r => r._id !== receiptId)
          }
        });
      }
      
      set({ expenses, isLoading: false });
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.error('Error deleting receipt:', error);
      throw error;
    }
  },

  // Add expense to invoice
  addToInvoice: async (expenseId, invoiceId) => {
    set({ isLoading: true, operationError: null });
    
    try {
      const updatedExpense = await expenseService.addToInvoice(expenseId, invoiceId);
      
      // Update expense in the list
      const expenses = get().expenses.map(exp => 
        exp._id === expenseId ? updatedExpense : exp
      );
      
      // Update current expense if it's the one being added to invoice
      const currentExpense = get().currentExpense;
      if (currentExpense && currentExpense._id === expenseId) {
        set({ currentExpense: updatedExpense });
      }
      
      set({ expenses, isLoading: false });
      return updatedExpense;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.error('Error adding expense to invoice:', error);
      throw error;
    }
  },

  // Fetch expense statistics
  fetchStatistics: async (filters = {}) => {
    set({ isLoading: true, operationError: null });
    
    try {
      const statistics = await expenseService.getExpenseStats(filters);
      set({ statistics, isLoading: false });
      return statistics;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.error('Error fetching expense statistics:', error);
      throw error;
    }
  },

  // Export expenses to Excel
  exportToExcel: async () => {
    set({ isLoading: true, operationError: null });
    
    try {
      const { filters } = get();
      await expenseService.exportExpenses(filters);
      set({ isLoading: false });
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.error('Error exporting expenses:', error);
      throw error;
    }
  },

  // Import expenses from Excel
  importFromExcel: async (file) => {
    set({ isLoading: true, operationError: null });
    
    try {
      const result = await expenseService.importExpenses(file);
      
      // Refresh the expense list after import
      await get().fetchExpenses();
      
      set({ isLoading: false });
      return result;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.error('Error importing expenses:', error);
      throw error;
    }
  },

  // Clear current expense
  clearCurrentExpense: () => set({ currentExpense: null }),

  // Clear error
  clearError: () => set({ criticalError: null, operationError: null })
}));

export default useExpenseStore;